import { useMemo, useState } from 'react';
import { CaretDownIcon, CaretRightIcon } from '@phosphor-icons/react';
import { Label } from 'src/components/ui/label';
import { Input } from 'src/components/ui/input';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'src/components/ui/collapsible';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import { SingleSelect } from 'src/components/common/SingleSelect';
import { T, useGT } from 'gt-react';
import type { DataSourceLinkSelection } from './useDataSourceLinkSelection';

export function DataSourceLinkStrategySelector({
  selection,
  disabled = false,
  withTableTemplate = false,
}: {
  selection: DataSourceLinkSelection;
  disabled?: boolean;
  /**
   * Adapter steps process one source blob and must resolve to a single link, so
   * they derive the target table from the input blob via a template string. The
   * Observe trigger leaves this off and fans out to every link of the schema.
   */
  withTableTemplate?: boolean;
}) {
  const gt = useGT();
  const [matchesOpen, setMatchesOpen] = useState(false);

  const {
    groupedLinks,
    allLinks,
    linksLoading,
    linksError,
    schemasData,
    strategy,
    selectedLinkId,
    selectedLink,
    matchingSchemaId,
    matchingTableTemplate,
    matchedLinks,
    handleStrategyChange,
    handleLinkChange,
    handleMatchingSchemaChange,
    handleMatchingTableTemplateChange,
  } = selection;

  const schemaOptions = useMemo(
    () => (schemasData ?? []).map((s) => ({ id: s.id, label: s.name })),
    [schemasData],
  );
  const selectedSchemaOption =
    schemaOptions.find((o) => o.id === matchingSchemaId) ?? null;

  // Map each link id to its source title (lost when groups are flattened) so the
  // matched-links list can show where each link lives.
  const sourceByLinkId = useMemo(() => {
    const map = new Map<string, string>();
    for (const group of groupedLinks) {
      for (const link of group.links) map.set(link.id, group.sourceTitle);
    }
    return map;
  }, [groupedLinks]);

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">
        <T context="label for datasource link strategy dropdown in datasource link selector">
          DataSource Link Strategy
        </T>
      </Label>
      <Select
        value={strategy ?? ''}
        onValueChange={handleStrategyChange}
        disabled={disabled}
      >
        <SelectTrigger>
          <SelectValue
            placeholder={gt('Choose a strategy', {
              context:
                'placeholder for datasource link strategy select in datasource link selector',
            })}
          />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="static">
            <T context="option label for static datasource link strategy in datasource link selector">
              Static
            </T>
          </SelectItem>
          <SelectItem value="matching_schema">
            <T context="option label for matching schema datasource link strategy in datasource link selector">
              Matching Schema
            </T>
          </SelectItem>
        </SelectContent>
      </Select>

      {strategy === 'static' && (
        <div className="space-y-2 pt-1">
          <Select
            value={selectedLinkId}
            onValueChange={handleLinkChange}
            disabled={linksLoading || linksError || disabled}
          >
            <SelectTrigger>
              <SelectValue
                placeholder={
                  linksLoading
                    ? gt('Loading…', {
                        context:
                          'loading state for datasource link select in datasource link selector',
                      })
                    : gt('Choose a DataSource Link', {
                        context:
                          'placeholder for datasource link select in datasource link selector',
                      })
                }
              />
            </SelectTrigger>
            <SelectContent>
              {groupedLinks.map((group) => (
                <SelectGroup key={group.sourceTitle}>
                  <SelectLabel>{group.sourceTitle}</SelectLabel>
                  {group.links.map((link) => (
                    <SelectItem key={link.id} value={link.id}>
                      {link.foreignSchemaName}.{link.foreignTableName}
                    </SelectItem>
                  ))}
                </SelectGroup>
              ))}
              {allLinks.length === 0 && !linksLoading && (
                <div className="px-2 py-1.5 text-sm text-muted-foreground">
                  {linksError
                    ? gt('Failed to load DataSource Links.', {
                        context:
                          'error state for datasource link select in datasource link selector',
                      })
                    : gt(
                        'No DataSource Links found. Add one in Settings > Data Sources.',
                        {
                          context:
                            'empty state for datasource link select in datasource link selector',
                        },
                      )}
                </div>
              )}
            </SelectContent>
          </Select>
          {selectedLink && (
            <div className="rounded-md border p-3 space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  <T context="label for schema definition name in datasource link selector details">
                    Schema
                  </T>
                </span>
                <span className="font-mono text-xs">
                  {selectedLink.schemaName}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  <T context="label for table field in datasource link selector details">
                    Table
                  </T>
                </span>
                <span className="font-mono text-xs">
                  {selectedLink.foreignSchemaName}.
                  {selectedLink.foreignTableName}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {strategy === 'matching_schema' && (
        <div className="space-y-2 pt-1">
          <SingleSelect
            options={schemaOptions}
            value={selectedSchemaOption}
            onChange={(opt) => handleMatchingSchemaChange(opt?.id ?? '')}
            disabled={disabled}
            placeholder={gt('Choose a schema', {
              context:
                'placeholder for matching schema select in datasource link selector',
            })}
            searchPlaceholder={gt('Search schemas', {
              context:
                'search placeholder for matching schema select in datasource link selector',
            })}
            fullWidth
            allowClear
          />

          {withTableTemplate && matchingSchemaId && (
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">
                <T context="label for matching schema table template input in datasource link selector">
                  Table Template
                </T>
                <span className="text-destructive">*</span>
              </Label>
              <T context="placeholder for matching schema table template input in datasource link selector">
                <Input
                  value={matchingTableTemplate}
                  placeholder="&#123;FILE&#125;"
                  onChange={(e) =>
                    handleMatchingTableTemplateChange(e.target.value)
                  }
                  disabled={disabled}
                  className="text-sm font-mono"
                />
              </T>
              <p className="text-xs text-muted-foreground">
                <T context="helper text for matching schema table template in datasource link selector">
                  Resolved against the input blob URI to select one DataSource
                  Link. Supports &#123;DIR&#125; and &#123;FILE&#125; (with
                  slice forms like &#123;FILE:8&#125; for the first 8 chars or
                  &#123;FILE:-8&#125; for the last 8).
                </T>
              </p>
            </div>
          )}

          {matchingSchemaId && (
            <Collapsible open={matchesOpen} onOpenChange={setMatchesOpen}>
              <CollapsibleTrigger className="flex items-center gap-1 text-sm text-muted-foreground w-full">
                {matchesOpen ? (
                  <CaretDownIcon className="size-4 shrink-0" />
                ) : (
                  <CaretRightIcon className="size-4 shrink-0" />
                )}
                <span>
                  {gt('{count} matching DataSource Links found', {
                    count: matchedLinks.length,
                    context:
                      'count of datasource links matching the selected schema',
                  })}
                </span>
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-2">
                {matchedLinks.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    <T context="empty matched links state in datasource link selector">
                      No DataSource Links conform to this schema yet.
                    </T>
                  </p>
                ) : (
                  <div className="space-y-1.5">
                    {matchedLinks.map((link) => (
                      <div
                        key={link.id}
                        className="rounded-md border p-2 text-xs space-y-0.5"
                      >
                        <div className="font-mono">
                          {link.foreignSchemaName}.{link.foreignTableName}
                        </div>
                        <div className="text-muted-foreground">
                          {sourceByLinkId.get(link.id)} · {link.schemaName}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CollapsibleContent>
            </Collapsible>
          )}

          <p className="text-xs text-muted-foreground">
            {withTableTemplate ? (
              <T context="helper text for matching schema with table template in adapter">
                Matches the single DataSource Link whose data sink the template
                resolves to. Also used to configure column mappings.
              </T>
            ) : (
              <T context="helper text for matching schema select in datasource link selector">
                Targets every DataSource Link that conforms to the selected
                schema. Also used to configure column mappings.
              </T>
            )}
          </p>
        </div>
      )}
    </div>
  );
}
